import React from "react";
import TextField from "@mui/material/TextField";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector, useDispatch } from "react-redux";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ua from "react-phone-number-input/locale/ua.json";
import { useState } from "react";
import schema from "./schema";
import { setPatientsBirthDay } from "@/store/patients/patients";
import { dateFormatter } from "@/utils/dateFormatter";

import { RootState } from "@/store/store";
import { inputStyles } from "@/utils/constants";
import InputFieldCalendar from "../../InputFieldCalendar/InputFieldCalendar";
import { ISearchDrawerProps } from "@/shared/interfaces/ui";
import { ICreatePatient } from "@/shared/interfaces/patientPage";

import useUpdateEntity from "@/hooks/useUpdateEntity";
import useCreateNewEntity from "@/hooks/useCreateNewEntity";
import Button from "@/shared/components/Button/Button";

import "./AppointmentsDrayewNewEntity.scss";

export default function AppointmentsDrayewNewEntity({
    toggleDrawer,
    isEditEntity,
}: ISearchDrawerProps) {
    const dispatch = useDispatch();
    const [phoneCount, setPhoneCount] = useState<number>(1);

    const patientsBirthDay = useSelector(
        (state: RootState) => state.patients.patientsBirthDay,
    );

    const isLocalLoading = useSelector(
        (state: RootState) => state.ui.isLocalLoading,
    );
    const patientData = useSelector(
        (state: RootState) => state.patients.selectedPatient,
    );
    const { handleUpdate } = useUpdateEntity();
    const { handleCreate } = useCreateNewEntity();

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
    } = useForm<ICreatePatient>({
        resolver: yupResolver(schema),
        defaultValues: {
            primaryPhone: "",
            primaryPhoneName: "",
            name: "",
            secondaryPhones: [],
            email: "",
            adress: "",
            dateOfBirth: patientsBirthDay.toString(),
            gender: "female",
            appointments: [],
            dentalFormulas: [],
            patientPhotos: [],
        },
    });

    useEffect(() => {
        if (isEditEntity && patientData) {
            reset({
                primaryPhone: patientData.primaryPhone,
                name: patientData.name,
                secondaryPhones: patientData.secondaryPhones,
                primaryPhoneName: patientData.primaryPhoneName,
                email: patientData.email,
                adress: patientData.adress,
                dateOfBirth: patientData.dateOfBirth,
                gender: patientData.gender,
                appointments: patientData.appointments,
                dentalFormulas: patientData.dentalFormulas,
                patientPhotos: patientData.patientPhotos,
                ...(patientData._id ? { _id: patientData._id } : {}),
            });
        }
    }, [isEditEntity, patientData, patientsBirthDay, reset]);

    const onSubmit = async (data: ICreatePatient): Promise<void> => {
        function handleClose() {
            toggleDrawer(false);
            reset();
            dispatch(setPatientsBirthDay(dateFormatter(new Date())));
        }
        const dataObj: ICreatePatient = {
            name: data.name,
            gender: data.gender,
            dateOfBirth: patientsBirthDay.toString(),
            email: data.email,
            adress: data.adress ? data.adress : "",
            primaryPhone: data.primaryPhone,
            primaryPhoneName: data.primaryPhoneName
                ? data.primaryPhoneName
                : "",
            secondaryPhones: data.secondaryPhones || [],
            appointments: data.appointments || [],
            dentalFormulas: data.dentalFormulas || [],
            patientPhotos: [],
            ...(patientData._id ? { _id: patientData._id } : {}),
        };

        if (!isEditEntity) {
            delete dataObj._id;
            await handleCreate({
                entity: "patients",
                data: dataObj,
                message: "Карту пацієнта успішно створено",
                onSuccess: () => handleClose(),
            });
        } else {
            await handleUpdate({
                entity: "patients",
                id: patientData._id ? patientData._id : "",
                data: dataObj,
                onSuccess: handleClose,
            });
        }
    };

    const { fields, append, remove } = useFieldArray({
        control,
        name: "secondaryPhones",
    });

    const handleAddPhone = () => {
        if (phoneCount <= 3) {
            setPhoneCount(prevState => prevState + 1);
            append({ number: "", type: "" });
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className='drawer_container_create_entity'
        >
            <div className='drawer_input_container'>
                <div className='input_container'>
                    <input
                        className='input_container_radio'
                        type='radio'
                        value='male'
                        id='male'
                        {...register("gender")}
                    />
                    <label htmlFor='male'>Чоловік</label>
                </div>
                <div className='input_container'>
                    <input
                        className='input_container_radio'
                        type='radio'
                        value='female'
                        id='female'
                        {...register("gender")}
                    />
                    <label htmlFor='female'>Жінка</label>
                </div>
            </div>

            <div className='drawer_form_container'>
                <TextField
                    type='text'
                    id='name'
                    label='ПІБ пацієнта'
                    variant='outlined'
                    {...register("name")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={{
                        ...inputStyles.root,
                        mb: "16px",
                        width: "100%",
                    }}
                />
                <InputFieldCalendar text={"Дата народження"} />
                <Controller
                    control={control}
                    name='primaryPhone'
                    render={({ field: { onChange, value } }) => (
                        <PhoneInput
                            defaultCountry='UA'
                            international
                            value={value}
                            onChange={onChange}
                            maxLength={16}
                            locales={"ua-UA"}
                            labels={ua}
                            className={`phone-input ${
                                errors.primaryPhone?.message
                                    ? "error_pfone"
                                    : "border"
                            } `}
                            label='Основний телефон'
                        />
                    )}
                />
                {errors.primaryPhone && (
                    <p
                        className='error_message'
                        style={{
                            margin: "2px 0 0 14px",
                            fontSize: "12px",
                            textAlign: "left",
                        }}
                    >
                        {errors.primaryPhone?.message}
                    </p>
                )}

                <TextField
                    type='text'
                    id='primaryPhoneName'
                    label='ПІБ власника телефону'
                    variant='outlined'
                    {...register("primaryPhoneName")}
                    sx={{
                        ...inputStyles.root,
                        width: "100%",
                        marginTop: "16px",
                    }}
                />

                <button
                    onClick={handleAddPhone}
                    className='drawer_form_button_add_pfone'
                >
                    + Додати телефон
                </button>
                {fields.map((field, index) => (
                    <React.Fragment key={field.id}>
                        <div
                            key={field.id}
                            className='phone-input phone-input_list'
                        >
                            <Controller
                                control={control}
                                name={`secondaryPhones.${index}.number`}
                                render={({ field: { onChange, value } }) => (
                                    <PhoneInput
                                        defaultCountry='UA'
                                        international
                                        value={value}
                                        onChange={onChange}
                                        maxLength={16}
                                        locales={"ua-UA"}
                                        labels={ua}
                                        className={`phone-input ${
                                            errors.secondaryPhones?.[index]
                                                ?.number
                                                ? "error_pfone"
                                                : "border"
                                        }`}
                                        label={`Телефон ${index + 2}`}
                                    />
                                )}
                            />
                            <TextField
                                type='text'
                                id={`secondaryPhones.${index}.number`}
                                variant='outlined'
                                sx={{ mb: "16px", width: "100%" }}
                                {...register(`secondaryPhones.${index}.type`)}
                            />

                            <button
                                type='button'
                                onClick={() => {
                                    remove(index);
                                    setPhoneCount(prevState => prevState - 1);
                                }}
                                className='service_item_delete'
                            ></button>
                        </div>
                        {errors?.secondaryPhones?.[index]?.number?.message && (
                            <p
                                className='error_message'
                                style={{
                                    margin: "-6px 0 10px 14px",
                                    fontSize: "12px",
                                    textAlign: "left",
                                }}
                            >
                                {errors.secondaryPhones[index]?.number?.message}
                            </p>
                        )}
                    </React.Fragment>
                ))}
                <TextField
                    type='email'
                    id='outlined-basic'
                    label='Електронна пошта'
                    variant='outlined'
                    {...register("email")}
                    error={!!errors.email}
                    sx={{
                        ...inputStyles.root,
                        mb: "16px",
                        width: "100%",
                    }}
                />

                <TextField
                    type='text'
                    id='adress'
                    label='Адреса пацієнта'
                    variant='outlined'
                    {...register("adress")}
                    error={!!errors.adress}
                    sx={{
                        ...inputStyles.root,
                        mb: "16px",
                        width: "100%",
                    }}
                />
            </div>
            <Button
                text={isEditEntity ? "Зберегти" : "Додати"}
                variant='approved'
                type='submit'
                disabled={isLocalLoading}
                isLoading={isLocalLoading}
            />
        </form>
    );
}
